/*
 * Copyright (c) 2023 Anvilor Solutions LLC - All Rights Reserved.
 *
 * The information and source code contained herein is the exclusive property of
 * Anvilor Solutions LLC and may not be disclosed, examined, or reproduced in whole
 * or in part without the explicit written authorization from Anvilor Solutions LLC.
 */

import { EelType } from "@anvilor/ngx-eel";
import { EelOperator } from "@anvilor/ngx-eel";
import { Evaluator } from "@anvilor/ngx-eel";
import { AnvilorFormsApp } from "../anvilor-forms-app-store.service";
import { Observable } from "rxjs";
import { getByPath } from "@anvilor/ngx-core-utils";
import { distinctUntilChangedJson } from "@anvilor/ngx-core-utils";

/**
 * Operators which take no parameter and are purely evaluating to a selectors
 * value.
 */
export class SelectorBasedOperator<T extends EelType = EelType> extends EelOperator<
  T,
  {}
> {
  private readonly _id: string;
  private readonly selector$: Observable<T>;

  constructor(id: string, selector$: Observable<T>) {
    super();
    this._id = id;
    this.selector$ = selector$;
  }

  get id(): string {
    return this._id;
  }

  eval$(evaluator: Evaluator, node: {}): Observable<any> {
    return this.selector$;
  }
}

/**
 * Operators which select from the state, and then have a path "p" parameter to
 * select from within that.
 *
 * NOTE - technically with the #get operator, this is not really necessary as
 *  we could just use the SelectorBasedOperator and then client expressions
 *  must use #get to select with their path, but we will leave this for now as
 *  (I think) it may offer some slight memoization-performance benefits.
 */
export class SelectorBasedOperatorWithPath<
  T extends EelType = EelType
> extends EelOperator<T, { path: string; default_value?: any }> {
  protected app: AnvilorFormsApp;
  private readonly _id: string;
  private base$: Observable<any>;

  constructor(id: string, app: AnvilorFormsApp, base$: Observable<any>) {
    super();
    this._id = id;
    this.app = app;
    this.base$ = base$;
  }

  get id(): string {
    return this._id;
  }

  eval$(
    evaluator: Evaluator,
    node: { path: string; default_value?: any }
  ): Observable<any> {
    return this.app.store
      .select(this.base$, (data) =>
        getByPath(data, node.path, { defaultValue: node.default_value ?? null })
      )
      .pipe(distinctUntilChangedJson());
  }
}
